import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation, Thumbs, Pagination } from "swiper";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import SwiperWrapper from "components/swiper-wrapper";
import { ExtraContactImage } from "components/listing-detail/info-modal-tabs/galery-tab/ExtraContactImage";
import { LazyImage } from "components/common/lazy-image/LazyImage";

import styles from "./styles.module.css";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

const GaleryTab = ({
  listingDetailData,
  imagesFullPath = [],
  phoneNumber,
  listingId,
  flatTypes,
  isUnitTypes,
  pageType,
}) => {
  if (!imagesFullPath.length) return <div>Maalesef Resim Bulunamadı</div>;

  const swiperRef = useRef(null);
  const activeImageIndex = useSelector((state) => state.listingDetail.activeImageIndex);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(activeImageIndex);

  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  const isProjectPage = pageType === "project";

  const extraContactPathUrl = imagesFullPath.slice(-1);
  const imagesFromAddExtraContactPath = [...imagesFullPath, ...extraContactPathUrl];
  const fullImagesPath = isUnitTypes || isProjectPage ? imagesFullPath : imagesFromAddExtraContactPath;
  const hasEnoughImages = imagesFullPath.length > 8;

  const handleSetSelectedImageIndex = (num) => {
    swiperRef.current?.swiper?.slideTo(num);
  };

  const imageWrapperClasses = classNames({
    [styles.imageWrapper]: !isUnitTypes,
    [styles.imageWrapperForFlatType]: isUnitTypes,
  });

  const imageClasses = classNames({
    [styles.image]: !isUnitTypes,
    [styles.imageForFlatType]: isUnitTypes,
  });

  const galeryPhotoShowEvent = (clickedElement) => {
    const clickedElementEquals = {
      "swiper-button-prev": "prev",
      "swiper-button-next": "next",
    };
    Ga4WithEventParameters({
      eventName: "click_button",
      eventParameters: { label: `galeri||${clickedElementEquals[clickedElement]}` },
    });
  };

  const handleClickSwiper = (event) => {
    const isNext = event.target.classList.contains("swiper-button-next");
    const isPrev = event.target.classList.contains("swiper-button-prev");

    if (isNext || isPrev) {
      setCurrentSlideIndex((prevIndex) => prevIndex + (isNext ? 1 : -1));
      galeryPhotoShowEvent(event.target.className);
    }
  };

  const handleKeyboardTrigger = (event) => {
    const direction = event?.key;

    if (direction === "ArrowLeft") {
      swiperRef.current?.swiper?.slidePrev();
    } else if (direction === "ArrowRight") {
      swiperRef.current?.swiper?.slideNext();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyboardTrigger, false);
    window.addEventListener("click", handleClickSwiper);
    return () => {
      window.removeEventListener("keydown", handleKeyboardTrigger);
      window.removeEventListener("click", handleClickSwiper);
    };
  }, []);

  useEffect(() => {
    handleSetSelectedImageIndex(activeImageIndex + 1);
    return () => {
      swiperRef.current?.swiper?.update();
    };
  }, [activeImageIndex]);

  const handleSlideClick = (event) => {
    const slideWidth = event.target.clientWidth;
    const clickX = event.nativeEvent.offsetX;

    if (clickX < slideWidth / 2) {
      swiperRef.current?.swiper?.slidePrev();
    } else {
      swiperRef.current?.swiper?.slideNext();
    }
  };

  useEffect(() => {
    if (isUnitTypes) {
      const totalItems = flatTypes?.length;
      const normalizedIndex = ((currentSlideIndex % totalItems) + totalItems) % totalItems;

      const unit = flatTypes[normalizedIndex];

      if (typeof window !== "undefined") {
        window.history.replaceState(null, "", `${window.location.pathname}#?unite-tipi-${unit.area}-${unit.roomCount}`);
      }
    }
  }, [currentSlideIndex]);

  return (
    <>
      <SwiperWrapper>
        <Swiper
          speed={0}
          ref={swiperRef}
          navigation
          slidesPerView={1}
          pagination={{
            type: "fraction",
            formatFractionTotal: (number) => {
              if (number === fullImagesPath.length) {
                return isUnitTypes || isProjectPage ? number : number - 1;
              }
            },
            formatFractionCurrent: (number) => {
              if (number === fullImagesPath.length) {
                return isUnitTypes || isProjectPage ? number : number - 1;
              } else {
                return number;
              }
            },
          }}
          modules={[FreeMode, Navigation, Thumbs, Pagination]}
          thumbs={{ swiper: thumbsSwiper }}
          loop
          threshold={50}
          className={styles.sliderWrapper}
        >
          <div className={styles.slideWrapper}>
            {fullImagesPath?.map((image, index) => {
              const lastImageIndex = imagesFromAddExtraContactPath.length - 1;
              const isShowLastImage = lastImageIndex === index;
              return (
                <SwiperSlide key={index}>
                  <div className={imageWrapperClasses} onClick={(e) => !isShowLastImage && handleSlideClick(e)}>
                    {isUnitTypes && (
                      <p className={styles.flatTypeText}>
                        {flatTypes[index]?.area} m²
                        <span className={styles.flatTypeTextSeparator}>|</span>
                        {flatTypes[index]?.roomType}
                        {flatTypes[index]?.flatPrice && (
                          <>
                            <span className={styles.flatTypeTextSeparator}>|</span>
                            <div className={styles.priceWrap}>
                              {new Intl.NumberFormat("tr-TR").format(flatTypes[index]?.flatPrice)}
                              {flatTypes[index]?.currency ? ` ${flatTypes[index]?.currency}` : " TL"}
                            </div>
                          </>
                        )}
                      </p>
                    )}
                    {isShowLastImage && !isUnitTypes && !isProjectPage ? (
                      <ExtraContactImage
                        listingDetailData={listingDetailData}
                        image={image}
                        phoneNumber={phoneNumber}
                        listingId={listingId}
                      />
                    ) : (
                      <LazyImage src={image} alt={image} className={imageClasses} visibleByDefault={index === 0} />
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </div>
        </Swiper>
      </SwiperWrapper>
      <Swiper
        navigation={false}
        className={!hasEnoughImages ? styles.thumbsListWrapperWithCenter : styles.thumbsListWrapper}
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        slidesPerView="auto"
        watchSlidesProgress
        allowTouchMove={false}
      >
        {imagesFullPath?.map((image, index) => {
          return (
            <SwiperSlide key={index} onClick={() => setCurrentSlideIndex(index)}>
              <div className={styles.thumbsWrapper}>
                <LazyImage src={image} alt={image} />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </>
  );
};

GaleryTab.propTypes = {
  imagesFullPath: PropTypes.array,
  phoneNumber: PropTypes.string,
  listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  listingDetailData: PropTypes.object,
  flatTypes: PropTypes.array,
  isUnitTypes: PropTypes.bool,
  pageType: PropTypes.string,
};

export default GaleryTab;
